/**
 * BUDGET ANALYTICS - Ultimate Interactive Budget Reporting & Tracking
 * 
 * Features:
 * - Company-wide overview with budget selector
 * - Interactive summary cards with click-through
 * - Charts: utilization bars, spend by GL type, monthly trend, vendor breakdown
 * - Expandable budget line table with inline drill-down
 * - Budget line detail modal with invoice list, approval history, monthly trend
 * - Invoice detail popup with link to All Documents
 * - Over-budget alerts panel
 * - Department breakdown with click-to-filter
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { budgetsAPI } from '../api/client';
import { useAuth } from '../context/AuthContext';
import {
  BarChart3, TrendingUp, TrendingDown, AlertTriangle, PoundSterling, RefreshCw,
  ChevronDown, ChevronRight, X, ExternalLink, FileText, Users, Building2,
  Target, Eye, Search, Filter, ArrowUpRight, ArrowDownRight, Activity,
  Receipt, ShieldCheck, Clock, CheckCircle, XCircle, Layers, ChevronUp
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend, Area, AreaChart
} from 'recharts';
import SortableTh from '../components/SortableTh';
import TablePagination from '../components/TablePagination';

const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];
const UTILIZATION_COLORS = { safe: '#10B981', warning: '#F59E0B', danger: '#EF4444' };

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
};

const getUtilColor = (pct) => {
  if (pct >= 100) return UTILIZATION_COLORS.danger;
  if (pct >= 80) return UTILIZATION_COLORS.warning;
  return UTILIZATION_COLORS.safe;
};

const getUtilBgClass = (pct) => {
  if (pct >= 100) return 'bg-red-100 text-red-800';
  if (pct >= 80) return 'bg-yellow-100 text-yellow-800';
  return 'bg-green-100 text-green-800';
};

const StatusBadge = ({ status }) => {
  const styles = {
    Approved: 'bg-green-100 text-green-800',
    Pending: 'bg-yellow-100 text-yellow-800',
    Rejected: 'bg-red-100 text-red-800',
    Posted: 'bg-blue-100 text-blue-800',
    Draft: 'bg-gray-100 text-gray-600',
  };
  const icons = {
    Approved: <CheckCircle className="w-3 h-3" />,
    Pending: <Clock className="w-3 h-3" />,
    Rejected: <XCircle className="w-3 h-3" />,
    Posted: <ShieldCheck className="w-3 h-3" />,
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-600'}`}>
      {icons[status]} {status}
    </span>
  );
};

// ─── INVOICE DETAIL POPUP ────────────────────────────────────────
const InvoiceDetailPopup = ({ invoice, approvals, onClose, onNavigate }) => {
  if (!invoice) return null;
  const invoiceApprovals = approvals?.filter(a => a.InvoiceID === invoice.InvoiceID) || [];

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-xl">
          <div className="text-white">
            <h3 className="text-lg font-bold flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              Invoice {invoice.InvoiceNumber}
            </h3>
            <p className="text-blue-100 text-sm mt-1">{invoice.VendorName}</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><span className="text-xs text-gray-500 block">Date</span><span className="font-medium">{invoice.InvoiceDate ? new Date(invoice.InvoiceDate).toLocaleDateString('en-GB') : '-'}</span></div>
            <div><span className="text-xs text-gray-500 block">Amount</span><span className="font-bold text-lg">{formatCurrency(invoice.Amount || invoice.LineTotal)}</span></div>
            <div><span className="text-xs text-gray-500 block">Status</span><StatusBadge status={invoice.Status || invoice.ApprovalStatus} /></div>
            <div><span className="text-xs text-gray-500 block">GL Account</span><span className="font-medium">{invoice.AccountCode} - {invoice.AccountName}</span></div>
            {invoice.LineDescription && (
              <div className="col-span-2"><span className="text-xs text-gray-500 block">Description</span><span>{invoice.LineDescription}</span></div>
            )}
            {invoice.GLAssignedByName && (
              <div><span className="text-xs text-gray-500 block">GL Assigned By</span><span>{invoice.GLAssignedByName}</span></div>
            )}
            {invoice.BudgetAssignedByName && (
              <div><span className="text-xs text-gray-500 block">Budget Assigned By</span><span>{invoice.BudgetAssignedByName}</span></div>
            )}
          </div>

          {invoiceApprovals.length > 0 && (
            <div>
              <h4 className="font-semibold text-sm text-gray-700 mb-2 flex items-center gap-1"><ShieldCheck className="w-4 h-4" /> Approval Trail</h4>
              <div className="space-y-2">
                {invoiceApprovals.map((a, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg text-sm">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                      a.ApprovalStatus === 'Approved' ? 'bg-green-500' : a.ApprovalStatus === 'Rejected' ? 'bg-red-500' : 'bg-gray-400'
                    }`}>{a.ApprovalOrder}</div>
                    <div className="flex-1">
                      <span className="font-medium">{a.ApproverName}</span>
                      {a.ApprovalDepartment && <span className="text-gray-500 ml-1">({a.ApprovalDepartment})</span>}
                    </div>
                    <StatusBadge status={a.ApprovalStatus} />
                    {a.ApprovedAt && <span className="text-xs text-gray-400">{new Date(a.ApprovedAt).toLocaleDateString('en-GB')}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-2 border-t">
            <button
              onClick={() => onNavigate(`/all-documents?search=${encodeURIComponent(invoice.InvoiceNumber)}`)}
              className="flex-1 btn-primary flex items-center justify-center gap-2 text-sm"
            >
              <ExternalLink className="w-4 h-4" /> View in All Documents
            </button>
            <button onClick={onClose} className="btn-secondary text-sm">Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── BUDGET LINE DRILL-DOWN MODAL ────────────────────────────────
const LineDrillDownModal = ({ budgetId, lineId, onClose, onNavigate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [sortField, setSortField] = useState('InvoiceDate');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await budgetsAPI.getLineDrillDown(budgetId, lineId);
        setData(res.data);
      } catch (err) {
        console.error('Drill-down error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [budgetId, lineId]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-xl p-8"><div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto" /></div>
      </div>
    );
  }

  if (!data) return null;
  const { line, invoices, approvals, monthlyTrend } = data;
  const utilPct = line.BudgetedAmount > 0 ? ((line.SpentAmount + line.CommittedAmount) / line.BudgetedAmount * 100) : 0;

  const sortedInvoices = [...invoices].sort((a, b) => {
    const aVal = a[sortField]; const bVal = b[sortField];
    if (sortField === 'InvoiceDate') return sortDir === 'desc' ? new Date(bVal) - new Date(aVal) : new Date(aVal) - new Date(bVal);
    if (typeof aVal === 'number') return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
    return sortDir === 'desc' ? String(bVal || '').localeCompare(String(aVal || '')) : String(aVal || '').localeCompare(String(bVal || ''));
  });

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronDown className="w-3 h-3 text-gray-300" />;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3 text-blue-600" /> : <ChevronDown className="w-3 h-3 text-blue-600" />;
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-5 border-b bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="text-white">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Layers className="w-6 h-6" />
                Budget Line Detail
              </h2>
              <p className="text-indigo-100 text-sm mt-1">
                {line.AccountCode && `${line.AccountCode} - `}{line.AccountName || line.Description}
                {line.DepartmentName && ` • ${line.DepartmentName}`}
                {line.ProjectName && ` • ${line.ProjectName}`}
              </p>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white p-1"><X className="w-6 h-6" /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Summary row */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <div className="text-xs text-blue-600 font-medium">Budgeted</div>
              <div className="text-lg font-bold text-blue-900">{formatCurrency(line.BudgetedAmount)}</div>
            </div>
            <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
              <div className="text-xs text-orange-600 font-medium">Spent</div>
              <div className="text-lg font-bold text-orange-900">{formatCurrency(line.SpentAmount)}</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
              <div className="text-xs text-purple-600 font-medium">Committed</div>
              <div className="text-lg font-bold text-purple-900">{formatCurrency(line.CommittedAmount)}</div>
            </div>
            <div className={`rounded-lg p-3 border ${line.RemainingAmount < 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
              <div className={`text-xs font-medium ${line.RemainingAmount < 0 ? 'text-red-600' : 'text-green-600'}`}>Remaining</div>
              <div className={`text-lg font-bold ${line.RemainingAmount < 0 ? 'text-red-900' : 'text-green-900'}`}>{formatCurrency(line.RemainingAmount)}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="text-xs text-gray-600 font-medium">Utilization</div>
              <div className={`text-lg font-bold ${utilPct >= 100 ? 'text-red-600' : utilPct >= 80 ? 'text-yellow-600' : 'text-green-600'}`}>
                {utilPct.toFixed(1)}%
              </div>
            </div>
          </div>

          {/* Utilization bar */}
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div className="h-3 rounded-full transition-all" style={{ width: `${Math.min(utilPct, 100)}%`, backgroundColor: getUtilColor(utilPct) }} />
          </div>

          {/* Monthly Trend Chart */}
          {monthlyTrend.length > 0 && (
            <div className="bg-white border rounded-lg p-4">
              <h4 className="font-semibold text-sm text-gray-700 mb-3 flex items-center gap-1"><Activity className="w-4 h-4" /> Monthly Spend Trend</h4>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={monthlyTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="MonthLabel" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `£${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                  <Area type="monotone" dataKey="Spent" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Invoices Table */}
          <div>
            <h4 className="font-semibold text-sm text-gray-700 mb-3 flex items-center gap-1">
              <FileText className="w-4 h-4" /> Invoices ({invoices.length})
            </h4>
            {invoices.length === 0 ? (
              <div className="text-center py-8 text-gray-400 bg-gray-50 rounded-lg">No invoices charged to this budget line yet</div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {[
                          { key: 'InvoiceNumber', label: 'Invoice' },
                          { key: 'VendorName', label: 'Vendor' },
                          { key: 'InvoiceDate', label: 'Date' },
                          { key: 'LineTotal', label: 'Amount' },
                          { key: 'Status', label: 'Status' },
                        ].map(col => (
                          <th key={col.key} onClick={() => handleSort(col.key)}
                            className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none">
                            <span className="flex items-center gap-1">{col.label} <SortIcon field={col.key} /></span>
                          </th>
                        ))}
                        <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {sortedInvoices.map((inv) => (
                        <tr key={inv.InvoiceLineID} className="hover:bg-blue-50 cursor-pointer transition-colors" onClick={() => setSelectedInvoice(inv)}>
                          <td className="px-3 py-2 text-sm font-medium text-blue-600">{inv.InvoiceNumber}</td>
                          <td className="px-3 py-2 text-sm">{inv.VendorName}</td>
                          <td className="px-3 py-2 text-sm text-gray-600">{inv.InvoiceDate ? new Date(inv.InvoiceDate).toLocaleDateString('en-GB') : '-'}</td>
                          <td className="px-3 py-2 text-sm font-medium text-right">{formatCurrency(inv.LineTotal)}</td>
                          <td className="px-3 py-2"><StatusBadge status={inv.ApprovalStatus || inv.Status} /></td>
                          <td className="px-3 py-2">
                            <button onClick={(e) => { e.stopPropagation(); onNavigate(`/all-documents?search=${encodeURIComponent(inv.InvoiceNumber)}`); }}
                              className="text-blue-500 hover:text-blue-700" title="View in Documents">
                              <ExternalLink className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td colSpan={3} className="px-3 py-2 text-sm font-bold text-gray-700">Total</td>
                        <td className="px-3 py-2 text-sm font-bold text-right">{formatCurrency(invoices.reduce((s, i) => s + (i.LineTotal || 0), 0))}</td>
                        <td colSpan={2}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </div>

          {line.CostCentre && (
            <div className="text-sm text-gray-500">Cost Centre: <span className="font-medium text-gray-700">{line.CostCentre}</span></div>
          )}
        </div>

        <div className="border-t p-4 bg-gray-50 flex justify-end">
          <button onClick={onClose} className="btn-secondary">Close</button>
        </div>
      </div>

      {selectedInvoice && (
        <InvoiceDetailPopup invoice={selectedInvoice} approvals={approvals} onClose={() => setSelectedInvoice(null)} onNavigate={onNavigate} />
      )}
    </div>
  );
};

// ─── MAIN BUDGET ANALYTICS COMPONENT ─────────────────────────────
export default function BudgetAnalytics() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const [companyData, setCompanyData] = useState(null);
  const [budgetData, setBudgetData] = useState(null);
  const [budgets, setBudgets] = useState([]);
  const [selectedBudgetId, setSelectedBudgetId] = useState(searchParams.get('budgetId') || '');
  const [loading, setLoading] = useState(true);
  const [drillDownLine, setDrillDownLine] = useState(null);
  const [expandedSections, setExpandedSections] = useState({ lines: true, vendors: true, departments: true, alerts: true, trend: true });
  const [lineSearch, setLineSearch] = useState('');
  const [view, setView] = useState('overview'); // overview | budget-detail
  const [overflowSummary, setOverflowSummary] = useState(null);
  const [budgetOverflow, setBudgetOverflow] = useState(null);
  const [resolvingLineId, setResolvingLineId] = useState(null);
  const [resolveNotes, setResolveNotes] = useState('');

  // Sort/page state for company overview budgets table
  const [budgetsSortBy, setBudgetsSortBy] = useState('BudgetName');
  const [budgetsSortDir, setBudgetsSortDir] = useState('asc');
  const handleBudgetsSort = (f) => { if (budgetsSortBy === f) setBudgetsSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setBudgetsSortBy(f); setBudgetsSortDir('asc'); } };

  // Sort/page state for budget detail lines table
  const [linesSortBy, setLinesSortBy] = useState('AccountCode');
  const [linesSortDir, setLinesSortDir] = useState('asc');
  const [linesPage, setLinesPage] = useState(1);
  const LINES_PAGE_SIZE = 50;
  const handleLinesSort = (f) => { if (linesSortBy === f) setLinesSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setLinesSortBy(f); setLinesSortDir('asc'); } setLinesPage(1); };

  const toggleSection = (key) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  const loadCompanyAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const [res, overflowRes] = await Promise.all([
        budgetsAPI.getCompanyAnalytics(),
        budgetsAPI.getOverflowSummary().catch(() => ({ data: { glAccounts: [], totals: {} } }))
      ]);
      setCompanyData(res.data);
      setBudgets(res.data.budgets || []);
      setOverflowSummary(overflowRes.data);
    } catch (err) {
      console.error('Company analytics error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadBudgetAnalytics = useCallback(async (budgetId) => {
    if (!budgetId) { setBudgetData(null); setBudgetOverflow(null); return; }
    try {
      setLoading(true);
      const [res, overflowRes] = await Promise.all([
        budgetsAPI.getAnalytics(budgetId),
        budgetsAPI.getBudgetOverflow(budgetId).catch(() => ({ data: { overflowLines: [], totalSpent: 0 } }))
      ]);
      setBudgetData(res.data);
      setBudgetOverflow(overflowRes.data);
    } catch (err) {
      console.error('Budget analytics error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCompanyAnalytics(); }, [loadCompanyAnalytics]);

  useEffect(() => {
    if (selectedBudgetId) {
      loadBudgetAnalytics(selectedBudgetId);
      setView('budget-detail');
    } else {
      setView('overview');
    }
  }, [selectedBudgetId, loadBudgetAnalytics]);

  const handleNavigate = (path) => navigate(path);

  const handleResolveOverflow = async (lineId, status) => {
    try {
      await budgetsAPI.resolveOverflow(lineId, { status, notes: resolveNotes });
      setResolvingLineId(null);
      setResolveNotes('');
      // Refresh
      if (selectedBudgetId) loadBudgetAnalytics(selectedBudgetId);
      else loadCompanyAnalytics();
    } catch (err) {
      console.error('Resolve overflow error:', err);
    }
  };

  if (loading && !companyData && !budgetData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-3" />
          <p className="text-gray-500">Loading budget analytics...</p>
        </div>
      </div>
    );
  }

  // ──────────── COMPANY OVERVIEW ────────────
  const renderCompanyOverview = () => {
    if (!companyData) return null;
    const { summary, budgets: budgetList, departments, glCategories, alerts } = companyData;
    const utilPct = summary.TotalBudgeted > 0 ? ((summary.TotalSpent + summary.TotalCommitted) / summary.TotalBudgeted * 100) : 0;

    const pieData = glCategories.map((g, i) => ({ name: g.AccountType, value: g.Budgeted || 0, color: CHART_COLORS[i % CHART_COLORS.length] }));
    const deptBarData = departments.map(d => ({
      name: d.DepartmentName?.length > 15 ? d.DepartmentName.substring(0, 15) + '...' : d.DepartmentName,
      Budgeted: d.Budgeted || 0,
      Spent: d.Spent || 0,
      Committed: d.Committed || 0,
    }));

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          <SummaryCard icon={<Target className="w-5 h-5" />} label="Total Budgeted" value={formatCurrency(summary.TotalBudgeted)} color="blue" />
          <SummaryCard icon={<TrendingUp className="w-5 h-5" />} label="Total Spent" value={formatCurrency(summary.TotalSpent)} color="orange" />
          <SummaryCard icon={<Clock className="w-5 h-5" />} label="Committed" value={formatCurrency(summary.TotalCommitted)} color="purple" />
          <SummaryCard icon={summary.TotalRemaining < 0 ? <TrendingDown className="w-5 h-5" /> : <PoundSterling className="w-5 h-5" />}
            label="Remaining" value={formatCurrency(summary.TotalRemaining)} color={summary.TotalRemaining < 0 ? 'red' : 'green'} />
          <SummaryCard icon={<Layers className="w-5 h-5" />} label="Active Budgets" value={summary.BudgetCount || 0} color="indigo" />
          <SummaryCard icon={<Receipt className="w-5 h-5" />} label="Tracked Invoices" value={summary.TrackedInvoices || 0} color="cyan" />
        </div>

        {/* Overall Utilization */}
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800">Overall Budget Utilization</h3>
            <span className={`text-2xl font-bold ${utilPct >= 100 ? 'text-red-600' : utilPct >= 80 ? 'text-yellow-600' : 'text-green-600'}`}>
              {utilPct.toFixed(1)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4">
            <div className="h-4 rounded-full transition-all duration-500" style={{ width: `${Math.min(utilPct, 100)}%`, backgroundColor: getUtilColor(utilPct) }} />
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>0%</span><span>50%</span><span>80%</span><span>100%</span>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* GL Type Pie */}
          {pieData.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border p-5">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-blue-600" /> Budget by GL Category</h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={3} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                    {pieData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={v => formatCurrency(v)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Department Bar */}
          {deptBarData.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border p-5">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><Building2 className="w-5 h-5 text-purple-600" /> Department Comparison</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={deptBarData} barCategoryGap="35%" maxBarSize={24}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `£${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={v => formatCurrency(v)} />
                  <Legend />
                  <Bar dataKey="Budgeted" fill="#3B82F6" radius={[5, 5, 0, 0]} />
                  <Bar dataKey="Spent" fill="#F59E0B" radius={[5, 5, 0, 0]} />
                  <Bar dataKey="Committed" fill="#8B5CF6" radius={[5, 5, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Budgets Table */}
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="p-5 border-b flex items-center justify-between">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2"><Layers className="w-5 h-5 text-indigo-600" /> Active Budgets</h3>
          </div>
          <div className="overflow-x-auto">
            {(() => {
              const sortedBudgetList = [...budgetList].sort((a, b) => {
                let av = a[budgetsSortBy], bv = b[budgetsSortBy];
                if (typeof av === 'number') return budgetsSortDir === 'asc' ? av - bv : bv - av;
                return budgetsSortDir === 'asc' ? String(av||'').localeCompare(String(bv||'')) : String(bv||'').localeCompare(String(av||''));
              });
              return (
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <SortableTh field="BudgetName" sortBy={budgetsSortBy} sortDir={budgetsSortDir} onSort={handleBudgetsSort}>Budget</SortableTh>
                  <SortableTh field="DepartmentName" sortBy={budgetsSortBy} sortDir={budgetsSortDir} onSort={handleBudgetsSort}>Department</SortableTh>
                  <SortableTh field="YearLabel" sortBy={budgetsSortBy} sortDir={budgetsSortDir} onSort={handleBudgetsSort}>Year</SortableTh>
                  <SortableTh field="Budgeted" sortBy={budgetsSortBy} sortDir={budgetsSortDir} onSort={handleBudgetsSort} align="right">Budgeted</SortableTh>
                  <SortableTh field="Spent" sortBy={budgetsSortBy} sortDir={budgetsSortDir} onSort={handleBudgetsSort} align="right">Spent</SortableTh>
                  <SortableTh field="Remaining" sortBy={budgetsSortBy} sortDir={budgetsSortDir} onSort={handleBudgetsSort} align="right">Remaining</SortableTh>
                  <SortableTh field="Utilization" sortBy={budgetsSortBy} sortDir={budgetsSortDir} onSort={handleBudgetsSort} align="right">Utilization</SortableTh>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedBudgetList.map((b) => {
                  const util = b.Utilization || 0;
                  return (
                    <tr key={b.BudgetID} className="hover:bg-blue-50 cursor-pointer transition-colors" onClick={() => setSelectedBudgetId(b.BudgetID)}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{b.BudgetName}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{b.DepartmentName || 'Company-wide'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{b.YearLabel}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(b.Budgeted)}</td>
                      <td className="px-4 py-3 text-sm text-right">{formatCurrency(b.Spent)}</td>
                      <td className={`px-4 py-3 text-sm text-right font-medium ${b.Remaining < 0 ? 'text-red-600' : ''}`}>{formatCurrency(b.Remaining)}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${getUtilBgClass(util)}`}>{util.toFixed(1)}%</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button className="text-blue-600 hover:text-blue-800" title="View Analytics">
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
              );})()}
          </div>
        </div>

        {/* Alerts */}
        {alerts.length > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-5">
            <h3 className="font-semibold text-orange-900 mb-3 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-orange-600" /> Budget Alerts ({alerts.length})</h3>
            <div className="space-y-2">
              {alerts.map((a, i) => (
                <div key={i} className="flex items-center justify-between bg-white rounded-lg p-3 border border-orange-100 cursor-pointer hover:shadow-sm"
                  onClick={() => setSelectedBudgetId(a.BudgetID)}>
                  <div>
                    <span className="font-medium text-gray-900">{a.AccountCode} {a.AccountName}</span>
                    {a.Description && <span className="text-gray-500 ml-2">- {a.Description}</span>}
                    <span className="text-gray-400 text-sm ml-2">({a.BudgetName})</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600">{formatCurrency(a.SpentAmount + a.CommittedAmount)} / {formatCurrency(a.BudgetedAmount)}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${a.Utilization >= 100 ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {a.Utilization}%{a.Utilization >= 100 && ' OVER'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Unbudgeted Expenditure (Overflow) */}
        {overflowSummary && overflowSummary.glAccounts?.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border">
            <div className="p-5 border-b bg-gradient-to-r from-red-50 to-orange-50">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Unbudgeted Expenditure
                </h3>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-600">{overflowSummary.totals.totalLines} overflow lines</span>
                  <span className="font-semibold text-red-600">{formatCurrency(overflowSummary.totals.totalSpent)} total</span>
                  {overflowSummary.totals.totalUnresolved > 0 && (
                    <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-bold">
                      {formatCurrency(overflowSummary.totals.totalUnresolved)} unresolved
                    </span>
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-1">Invoices that were approved but didn't match any budget line — auto-captured as overflow.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">GL Account</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Lines</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Budgets Affected</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Spent</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unresolved</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">First Seen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {overflowSummary.glAccounts.map((gl) => (
                    <tr key={gl.GLAccountID} className="hover:bg-red-50/30">
                      <td className="px-4 py-3">
                        <span className="font-medium text-gray-900">{gl.AccountCode}</span>
                        <span className="text-gray-500 ml-2">{gl.AccountName}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{gl.AccountType || '-'}</td>
                      <td className="px-4 py-3 text-right">{gl.OverflowLineCount}</td>
                      <td className="px-4 py-3 text-right">{gl.AffectedBudgets}</td>
                      <td className="px-4 py-3 text-right font-medium text-red-600">{formatCurrency(gl.TotalSpent)}</td>
                      <td className="px-4 py-3 text-right">
                        {gl.UnresolvedCount > 0 ? (
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-bold">{gl.UnresolvedCount}</span>
                        ) : (
                          <span className="text-green-600 text-xs font-medium">Resolved</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{gl.FirstOccurrence ? new Date(gl.FirstOccurrence).toLocaleDateString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {overflowSummary.message && (
              <div className="p-4 border-t bg-gray-50 text-sm text-gray-600 italic">
                💡 {overflowSummary.message}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ──────────── SINGLE BUDGET DETAIL ────────────
  const renderBudgetDetail = () => {
    if (!budgetData) return null;
    const { budget, lines, glBreakdown, vendors, monthlyTrend, recentTransactions, alerts, departmentBreakdown } = budgetData;

    const utilPct = budget.TotalAllocated > 0 
      ? ((budget.TotalSpent + budget.TotalCommitted) / budget.TotalAllocated * 100) 
      : 0;

    const filteredLines = lines.filter(l => {
      if (!lineSearch) return true;
      const s = lineSearch.toLowerCase();
      return (l.AccountCode || '').toLowerCase().includes(s)
        || (l.AccountName || '').toLowerCase().includes(s)
        || (l.Description || '').toLowerCase().includes(s)
        || (l.LineDepartmentName || '').toLowerCase().includes(s);
    });

    const pieData = glBreakdown.map((g, i) => ({ name: g.AccountType, value: g.Budgeted || 0, color: CHART_COLORS[i % CHART_COLORS.length] }));

    return (
      <div className="space-y-6">
        {/* Budget Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold">{budget.BudgetName}</h2>
              <p className="text-indigo-100 mt-1">
                {budget.DepartmentName || 'Company-wide'} • {budget.YearLabel}
                {budget.BudgetType && ` • ${budget.BudgetType}`}
                <span className={`ml-3 px-2 py-0.5 rounded text-xs font-semibold ${budget.Status === 'Active' ? 'bg-green-500/30' : 'bg-gray-500/30'}`}>
                  {budget.Status}
                </span>
              </p>
              {budget.CreatedByName && <p className="text-indigo-200 text-sm mt-1">Created by {budget.CreatedByName}</p>}
            </div>
            <div className="text-right">
              <p className="text-indigo-200 text-sm">Total Budget</p>
              <p className="text-3xl font-bold">{formatCurrency(budget.TotalBudget || budget.TotalAllocated)}</p>
            </div>
          </div>
        </div>

        {/* Summary Strip */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <SummaryCard icon={<Target className="w-5 h-5" />} label="Allocated" value={formatCurrency(budget.TotalAllocated)} color="blue" />
          <SummaryCard icon={<TrendingUp className="w-5 h-5" />} label="Spent" value={formatCurrency(budget.TotalSpent)} color="orange" />
          <SummaryCard icon={<Clock className="w-5 h-5" />} label="Committed" value={formatCurrency(budget.TotalCommitted)} color="purple" />
          <SummaryCard icon={budget.TotalRemaining < 0 ? <AlertTriangle className="w-5 h-5" /> : <PoundSterling className="w-5 h-5" />}
            label="Remaining" value={formatCurrency(budget.TotalRemaining)} color={budget.TotalRemaining < 0 ? 'red' : 'green'} />
          <SummaryCard icon={<Layers className="w-5 h-5" />} label="Budget Lines" value={budget.LineCount} color="indigo" />
        </div>

        {/* Utilization */}
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-800">Budget Utilization</h3>
            <span className={`text-2xl font-bold ${utilPct >= 100 ? 'text-red-600' : utilPct >= 80 ? 'text-yellow-600' : 'text-green-600'}`}>
              {utilPct.toFixed(1)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4">
            <div className="h-4 rounded-full transition-all duration-500" style={{ width: `${Math.min(utilPct, 100)}%`, backgroundColor: getUtilColor(utilPct) }} />
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Monthly Trend */}
          {monthlyTrend.length > 0 && (
            <CollapsibleSection title="Monthly Spend Trend" icon={<Activity className="w-5 h-5 text-green-600" />} expanded={expandedSections.trend} onToggle={() => toggleSection('trend')}>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={monthlyTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="MonthLabel" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `£${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={v => formatCurrency(v)} labelFormatter={l => l} />
                  <Area type="monotone" dataKey="Spent" stroke="#10B981" fill="#10B981" fillOpacity={0.2} name="Spend" />
                </AreaChart>
              </ResponsiveContainer>
            </CollapsibleSection>
          )}

          {/* GL Breakdown Pie */}
          {pieData.length > 0 && (
            <CollapsibleSection title="GL Category Breakdown" icon={<BarChart3 className="w-5 h-5 text-blue-600" />} expanded={true}>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={3} dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                    {pieData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={v => formatCurrency(v)} />
                </PieChart>
              </ResponsiveContainer>
            </CollapsibleSection>
          )}
        </div>

        {/* Budget Lines - Interactive Table */}
        <CollapsibleSection title={`Budget Lines (${filteredLines.length})`} icon={<Layers className="w-5 h-5 text-indigo-600" />}
          expanded={expandedSections.lines} onToggle={() => toggleSection('lines')}
          toolbar={
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
              <input type="text" placeholder="Search lines..." value={lineSearch} onChange={e => setLineSearch(e.target.value)}
                className="pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 w-56" />
            </div>
          }>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortableTh field="AccountCode" sortBy={linesSortBy} sortDir={linesSortDir} onSort={handleLinesSort}>GL Account</SortableTh>
                  <SortableTh field="Description" sortBy={linesSortBy} sortDir={linesSortDir} onSort={handleLinesSort}>Description</SortableTh>
                  <SortableTh field="LineDepartmentName" sortBy={linesSortBy} sortDir={linesSortDir} onSort={handleLinesSort}>Department</SortableTh>
                  <SortableTh field="BudgetedAmount" sortBy={linesSortBy} sortDir={linesSortDir} onSort={handleLinesSort} align="right">Budgeted</SortableTh>
                  <SortableTh field="SpentAmount" sortBy={linesSortBy} sortDir={linesSortDir} onSort={handleLinesSort} align="right">Spent</SortableTh>
                  <SortableTh field="CommittedAmount" sortBy={linesSortBy} sortDir={linesSortDir} onSort={handleLinesSort} align="right">Committed</SortableTh>
                  <SortableTh field="RemainingAmount" sortBy={linesSortBy} sortDir={linesSortDir} onSort={handleLinesSort} align="right">Remaining</SortableTh>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Utilization</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Invoices</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Drill In</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(() => {
                  const sorted = [...filteredLines].sort((a, b) => {
                    let av = a[linesSortBy], bv = b[linesSortBy];
                    if (typeof av === 'number' || linesSortBy.endsWith('Amount')) return linesSortDir === 'asc' ? (av||0)-(bv||0) : (bv||0)-(av||0);
                    return linesSortDir === 'asc' ? String(av||'').localeCompare(String(bv||'')) : String(bv||'').localeCompare(String(av||''));
                  });
                  const paged = sorted.slice((linesPage - 1) * LINES_PAGE_SIZE, linesPage * LINES_PAGE_SIZE);
                  if (paged.length === 0) return <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-400">{lineSearch ? 'No matching lines found' : 'No budget lines'}</td></tr>;
                  return paged.map(line => {
                    const util = line.BudgetedAmount > 0 ? ((line.SpentAmount + line.CommittedAmount) / line.BudgetedAmount * 100) : 0;
                    return (
                      <tr key={line.BudgetLineID} className="hover:bg-indigo-50 transition-colors cursor-pointer" onClick={() => setDrillDownLine(line)}>
                        <td className="px-4 py-3 text-sm">
                          {line.AccountCode && <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded mr-1">{line.AccountCode}</span>}
                          <span className="text-gray-700">{line.AccountName}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 max-w-[200px] truncate" title={line.Description}>{line.Description || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{line.LineDepartmentName || '-'}</td>
                        <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(line.BudgetedAmount)}</td>
                        <td className="px-4 py-3 text-sm text-right">{formatCurrency(line.SpentAmount)}</td>
                        <td className="px-4 py-3 text-sm text-right text-purple-600">{formatCurrency(line.CommittedAmount)}</td>
                        <td className={`px-4 py-3 text-sm text-right font-medium ${line.RemainingAmount < 0 ? 'text-red-600' : ''}`}>{formatCurrency(line.RemainingAmount)}</td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-200 rounded-full h-2 min-w-[50px]">
                              <div className="h-2 rounded-full" style={{ width: `${Math.min(util, 100)}%`, backgroundColor: getUtilColor(util) }} />
                            </div>
                            <span className={`text-xs font-semibold ${util >= 100 ? 'text-red-600' : util >= 80 ? 'text-yellow-600' : 'text-green-600'}`}>
                              {util.toFixed(0)}%
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                            <FileText className="w-3 h-3" /> {line.InvoiceCount}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-100 p-1 rounded" title="Drill into details">
                            <ArrowUpRight className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
              {filteredLines.length > 0 && (
                <tfoot className="bg-gray-50 font-bold">
                  <tr>
                    <td colSpan={3} className="px-4 py-3 text-sm text-gray-700">Totals ({filteredLines.length} lines)</td>
                    <td className="px-4 py-3 text-sm text-right">{formatCurrency(filteredLines.reduce((s, l) => s + (l.BudgetedAmount || 0), 0))}</td>
                    <td className="px-4 py-3 text-sm text-right">{formatCurrency(filteredLines.reduce((s, l) => s + (l.SpentAmount || 0), 0))}</td>
                    <td className="px-4 py-3 text-sm text-right text-purple-600">{formatCurrency(filteredLines.reduce((s, l) => s + (l.CommittedAmount || 0), 0))}</td>
                    <td className="px-4 py-3 text-sm text-right">{formatCurrency(filteredLines.reduce((s, l) => s + (l.RemainingAmount || 0), 0))}</td>
                    <td colSpan={3}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          <TablePagination page={linesPage} pageSize={LINES_PAGE_SIZE} total={filteredLines.length} onPageChange={setLinesPage} />
        </CollapsibleSection>
        {vendors.length > 0 && (
          <CollapsibleSection title={`Top Vendors (${vendors.length})`} icon={<Users className="w-5 h-5 text-orange-600" />}
            expanded={expandedSections.vendors} onToggle={() => toggleSection('vendors')}>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Invoices</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Spend</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Invoice</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">GL Accounts</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">View</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {vendors.map((v, i) => (
                    <tr key={i} className="hover:bg-orange-50 transition-colors">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{v.VendorName}</td>
                      <td className="px-4 py-3 text-sm text-right">{v.InvoiceCount}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(v.TotalSpend)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{v.LastInvoiceDate ? new Date(v.LastInvoiceDate).toLocaleDateString('en-GB') : '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 max-w-[250px] truncate" title={v.GLAccounts}>{v.GLAccounts || '-'}</td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => handleNavigate(`/all-documents?search=${encodeURIComponent(v.VendorName)}`)}
                          className="text-blue-600 hover:text-blue-800" title="View vendor invoices">
                          <ExternalLink className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CollapsibleSection>
        )}

        {/* Department Breakdown */}
        {departmentBreakdown.length > 1 && (
          <CollapsibleSection title="Department Breakdown" icon={<Building2 className="w-5 h-5 text-purple-600" />}
            expanded={expandedSections.departments} onToggle={() => toggleSection('departments')}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {departmentBreakdown.map((d, i) => {
                const dUtil = d.Budgeted > 0 ? ((d.Spent + d.Committed) / d.Budgeted * 100) : 0;
                return (
                  <div key={i} className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-800 text-sm">{d.DepartmentName}</h4>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${getUtilBgClass(dUtil)}`}>{dUtil.toFixed(0)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                      <div className="h-2 rounded-full" style={{ width: `${Math.min(dUtil, 100)}%`, backgroundColor: getUtilColor(dUtil) }} />
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><span className="text-gray-500">Budgeted:</span> <span className="font-medium">{formatCurrency(d.Budgeted)}</span></div>
                      <div><span className="text-gray-500">Spent:</span> <span className="font-medium">{formatCurrency(d.Spent)}</span></div>
                      <div><span className="text-gray-500">Committed:</span> <span className="font-medium">{formatCurrency(d.Committed)}</span></div>
                      <div><span className="text-gray-500">Remaining:</span> <span className={`font-medium ${d.Remaining < 0 ? 'text-red-600' : ''}`}>{formatCurrency(d.Remaining)}</span></div>
                    </div>
                    <div className="text-xs text-gray-400 mt-2">{d.LineCount} budget line(s)</div>
                  </div>
                );
              })}
            </div>
          </CollapsibleSection>
        )}

        {/* Recent Transactions */}
        {recentTransactions.length > 0 && (
          <CollapsibleSection title="Recent Transactions" icon={<Receipt className="w-5 h-5 text-cyan-600" />} expanded={false}>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">GL Account</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Budget Line</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">View</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {recentTransactions.map((t, i) => (
                    <tr key={i} className="hover:bg-cyan-50 transition-colors">
                      <td className="px-4 py-3 text-sm font-medium text-blue-600 cursor-pointer hover:underline"
                        onClick={() => handleNavigate(`/all-documents?search=${encodeURIComponent(t.InvoiceNumber)}`)}>{t.InvoiceNumber}</td>
                      <td className="px-4 py-3 text-sm">{t.VendorName}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{t.InvoiceDate ? new Date(t.InvoiceDate).toLocaleDateString('en-GB') : '-'}</td>
                      <td className="px-4 py-3 text-sm">{t.AccountCode} {t.AccountName}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 max-w-[150px] truncate">{t.BudgetLineDescription || '-'}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(t.Amount)}</td>
                      <td className="px-4 py-3 text-center"><StatusBadge status={t.ApprovalStatus || t.Status} /></td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => handleNavigate(`/all-documents?search=${encodeURIComponent(t.InvoiceNumber)}`)} className="text-blue-600 hover:text-blue-800">
                          <ExternalLink className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CollapsibleSection>
        )}

        {/* Alerts */}
        {alerts.length > 0 && (
          <CollapsibleSection title={`Budget Alerts (${alerts.length})`} icon={<AlertTriangle className="w-5 h-5 text-red-600" />}
            expanded={expandedSections.alerts} onToggle={() => toggleSection('alerts')}>
            <div className="space-y-2">
              {alerts.map((a, i) => {
                const util = a.Utilization || 0;
                return (
                  <div key={i} className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer hover:shadow-sm ${
                    util >= 100 ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'
                  }`} onClick={() => setDrillDownLine(a)}>
                    <div className="flex items-center gap-3">
                      {util >= 100 ? <XCircle className="w-5 h-5 text-red-500" /> : <AlertTriangle className="w-5 h-5 text-yellow-500" />}
                      <div>
                        <span className="font-medium text-gray-900">{a.AccountCode} {a.AccountName}</span>
                        {a.Description && <span className="text-gray-500 ml-1">- {a.Description}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-600">{formatCurrency(a.SpentAmount + a.CommittedAmount)} / {formatCurrency(a.BudgetedAmount)}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${util >= 100 ? 'bg-red-200 text-red-800' : 'bg-yellow-200 text-yellow-800'}`}>
                        {util}%
                      </span>
                      <ArrowUpRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                );
              })}
            </div>
          </CollapsibleSection>
        )}

        {/* Overflow / Unbudgeted Lines */}
        {budgetOverflow && budgetOverflow.overflowLines?.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-red-200">
            <div className="p-5 border-b bg-gradient-to-r from-red-50 to-orange-50">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Unbudgeted Expenditure ({budgetOverflow.overflowLines.length})
                </h3>
                <span className="font-semibold text-red-600">{formatCurrency(budgetOverflow.totalSpent)}</span>
              </div>
              <p className="text-sm text-gray-500 mt-1">These invoice lines were auto-captured because they didn't match any budget line.</p>
            </div>
            <div className="divide-y divide-gray-100">
              {budgetOverflow.overflowLines.map((line) => (
                <div key={line.BudgetLineID} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{line.AccountCode} {line.AccountName}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          line.OverflowStatus === 'Unresolved' ? 'bg-red-100 text-red-700' :
                          line.OverflowStatus === 'Acknowledged' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-green-100 text-green-700'
                        }`}>{line.OverflowStatus}</span>
                      </div>
                      {line.Description && <p className="text-sm text-gray-500 mt-0.5">{line.Description}</p>}
                      <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
                        {line.TriggerInvoiceNumber && (
                          <span className="flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            Triggered by {line.TriggerInvoiceNumber}
                            {line.TriggerVendorName && ` (${line.TriggerVendorName})`}
                          </span>
                        )}
                        <span>{line.LinkedInvoices || 0} invoice(s) linked</span>
                        <span>{new Date(line.CreatedAt).toLocaleDateString()}</span>
                      </div>
                      {line.ResolvedByName && (
                        <p className="text-xs text-green-600 mt-1">
                          Resolved by {line.ResolvedByName} on {new Date(line.OverflowResolvedAt).toLocaleDateString()}
                          {line.OverflowResolutionNotes && ` — ${line.OverflowResolutionNotes}`}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      <span className="text-lg font-bold text-red-600">{formatCurrency(line.SpentAmount)}</span>
                      {line.OverflowStatus === 'Unresolved' && (
                        resolvingLineId === line.BudgetLineID ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={resolveNotes}
                              onChange={e => setResolveNotes(e.target.value)}
                              placeholder="Notes (optional)"
                              className="px-2 py-1 text-sm border rounded w-40"
                            />
                            <button onClick={() => handleResolveOverflow(line.BudgetLineID, 'Acknowledged')}
                              className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200">
                              Acknowledge
                            </button>
                            <button onClick={() => handleResolveOverflow(line.BudgetLineID, 'Resolved')}
                              className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200">
                              Resolve
                            </button>
                            <button onClick={() => { setResolvingLineId(null); setResolveNotes(''); }}
                              className="text-gray-400 hover:text-gray-600">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => setResolvingLineId(line.BudgetLineID)}
                            className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 font-medium">
                            Resolve
                          </button>
                        )
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ──────────── RENDER ────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-indigo-600" />
            Budget Analytics
          </h1>
          <p className="text-gray-600 mt-1">Interactive budget reporting, tracking & management</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Budget Selector */}
          <select value={selectedBudgetId} onChange={e => setSelectedBudgetId(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 min-w-[250px]">
            <option value="">Company Overview</option>
            {budgets.map(b => (
              <option key={b.BudgetID} value={b.BudgetID}>{b.BudgetName} - {b.DepartmentName || 'Company'} ({b.YearLabel})</option>
            ))}
          </select>
          <button onClick={() => selectedBudgetId ? loadBudgetAnalytics(selectedBudgetId) : loadCompanyAnalytics()}
            className="btn-secondary flex items-center gap-2 text-sm" disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* View Toggle Breadcrumb */}
      {selectedBudgetId && (
        <div className="flex items-center gap-2 text-sm">
          <button onClick={() => setSelectedBudgetId('')} className="text-blue-600 hover:underline">Company Overview</button>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <span className="text-gray-700 font-medium">{budgetData?.budget?.BudgetName || 'Loading...'}</span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full" />
        </div>
      ) : view === 'overview' ? renderCompanyOverview() : renderBudgetDetail()}

      {/* Drill-down modal */}
      {drillDownLine && (
        <LineDrillDownModal
          budgetId={selectedBudgetId}
          lineId={drillDownLine.BudgetLineID}
          onClose={() => setDrillDownLine(null)}
          onNavigate={handleNavigate}
        />
      )}
    </div>
  );
}

// ─── REUSABLE COMPONENTS ─────────────────────────────────────────

function SummaryCard({ icon, label, value, color }) {
  const borderColor = {
    blue: 'border-blue-500', orange: 'border-orange-500', purple: 'border-purple-500',
    green: 'border-green-500', red: 'border-red-500', indigo: 'border-indigo-500', cyan: 'border-cyan-500',
  };
  const iconColor = {
    blue: 'text-blue-500', orange: 'text-orange-500', purple: 'text-purple-500',
    green: 'text-green-500', red: 'text-red-500', indigo: 'text-indigo-500', cyan: 'text-cyan-500',
  };

  return (
    <div className={`bg-white rounded-lg shadow p-4 border-l-4 ${borderColor[color] || 'border-gray-400'}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-xl font-bold text-gray-900 mt-1">{value}</p>
        </div>
        <div className={iconColor[color] || 'text-gray-500'}>{icon}</div>
      </div>
    </div>
  );
}

function CollapsibleSection({ title, icon, expanded = true, onToggle, toolbar, children }) {
  const [isExpanded, setIsExpanded] = useState(expanded);
  const toggle = onToggle || (() => setIsExpanded(prev => !prev));
  const open = onToggle ? expanded : isExpanded;

  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
      <div className="p-5 border-b flex items-center justify-between cursor-pointer" onClick={toggle}>
        <h3 className="font-semibold text-gray-800 flex items-center gap-2">{icon} {title}</h3>
        <div className="flex items-center gap-3">
          {toolbar && <div onClick={e => e.stopPropagation()}>{toolbar}</div>}
          {open ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </div>
      </div>
      {open && <div className="p-5">{children}</div>}
    </div>
  );
}
